import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const KEY = "bes_intro_seen_v2";

/** Premium loading screen: matte black + gold hairline + wordmark reveal, then curtain rises. */
export default function IntroReveal() {
  const [phase, setPhase] = useState("assemble");
  const [enabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem(KEY);
  });

  useEffect(() => {
    if (!enabled) { setPhase("done"); return; }
    const t1 = setTimeout(() => setPhase("hold"), 1400);
    const t2 = setTimeout(() => setPhase("rise"), 2100);
    const t3 = setTimeout(() => {
      setPhase("done");
      try { sessionStorage.setItem(KEY, "1"); } catch {}
    }, 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [enabled]);

  if (phase === "done" || !enabled) return null;

  const chars = ["B", "e", "s", "p", "o", "k", "e"];

  return (
    <AnimatePresence>
      <motion.div
        key="intro"
        className="fixed inset-0 z-[100000] flex items-center justify-center overflow-hidden"
        style={{ background: "#020202" }}
        initial={{ y: 0 }}
        animate={phase === "rise" ? { y: "-100%" } : { y: 0 }}
        transition={{ duration: 1.0, ease: [0.85, 0, 0.15, 1] }}
      >
        {/* Ambient gold radial */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(600px 400px at 50% 50%, rgba(198,168,124,0.10), transparent 60%)",
        }} />

        {/* Grain */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-overlay" style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }} />

        {/* Top-left status */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: phase !== "rise" ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          className="absolute top-10 left-10 font-mono text-[10px] uppercase tracking-[0.44em]"
          style={{ color: "var(--gold)" }}
        >
          <div className="flex items-center gap-3">
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--gold)", boxShadow: "0 0 12px var(--gold)" }}
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
            Loading · Reel 001
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: phase !== "rise" ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          className="absolute top-10 right-10 font-mono text-[10px] uppercase tracking-[0.44em]"
          style={{ color: "var(--text-3)" }}
        >
          Est. 2013 · Veteran Owned
        </motion.div>

        {/* Wordmark — assembling letters */}
        <div className="relative flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 120 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="h-px mb-10"
            style={{ background: "linear-gradient(90deg, transparent, var(--gold), transparent)" }}
          />

          <div className="flex items-baseline overflow-hidden px-6">
            {chars.map((ch, i) => (
              <motion.span
                key={i}
                initial={{ y: "110%", opacity: 0, filter: "blur(8px)" }}
                animate={{ y: "0%", opacity: 1, filter: "blur(0px)" }}
                transition={{ duration: 0.9, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] }}
                className="font-display leading-none inline-block"
                style={{
                  fontSize: "clamp(3.5rem, 13vw, 12rem)",
                  color: "var(--text)",
                  letterSpacing: "-0.045em",
                  textShadow: "0 0 60px rgba(198,168,124,0.2)",
                }}
              >
                {ch}
              </motion.span>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: phase === "hold" ? 1 : 0, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 font-mono text-[10px] uppercase tracking-[0.6em]"
            style={{ color: "var(--gold)" }}
          >
            Exterior · Solutions
          </motion.div>
        </div>

        {/* Bottom counter */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: phase !== "rise" ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.44em]"
          style={{ color: "var(--text-3)" }}
        >
          NYC · North Carolina
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
